import { ActionIcon, Avatar, Badge, Tooltip } from '@mantine/core';
import { IconChevronDown, IconCircleKeyFilled, IconSparkles, IconStrikethrough } from '@tabler/icons-react';
import type { SelectedStep } from '~/server/logic/prisma/step-select';
import type { AiChange } from '~/types';
import { match } from 'ts-pattern';
import clsx from 'clsx';
import IconAIEdit from '~/components/icons/IconAIEdit';
import { MarkdownStepDescription } from '~/components/common/MarkdownStepDescription';

export default function ExecutionStepHeader({
  step,
  expanded,
  setExpanded,
  aiChanges,
}: {
  step: SelectedStep;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  aiChanges?: AiChange[];
}) {
  const stepHealSuccess = aiChanges?.every((c) => c.isSuccess);

  return (
    <div
      className={clsx(
        'relative flex h-full cursor-pointer items-center px-4 py-2 transition-colors',
        'group-hover:bg-[var(--mantine-color-default-hover)]',
        {
          'opacity-50': step.skip,
        },
      )}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex h-full flex-1 items-center space-x-3">
        {!!step.skip && (
          <Tooltip label="Step was skipped">
            <Avatar
              size="sm"
              radius="xl"
              className="opacity-60 hover:opacity-80"
            >
              <IconStrikethrough size={14} />
            </Avatar>
          </Tooltip>
        )}

        <div className="flex items-center space-x-2">
          {match(step.mode)
            .with('STRICT', () => (
              <IconCircleKeyFilled
                className="flex-shrink-0 text-[var(--mantine-color-dimmed)]"
                size={14}
              />
            ))
            .with('AI_DYNAMIC', () => <IconAIEdit className="h-3.5 flex-shrink-0 text-[var(--mantine-color-dimmed)]" />)
            .otherwise(() => null)}
          <MarkdownStepDescription content={step.description} />
        </div>

        {!!aiChanges && aiChanges.length > 0 && (
          <Tooltip label={stepHealSuccess ? 'AI successfully healed this step' : 'AI attempted to heal this step'}>
            <Badge
              variant="outline"
              size="sm"
              color={stepHealSuccess ? 'blue' : 'orange'}
              className={
                stepHealSuccess ?
                  'ml-2 flex-shrink-0 text-blue-600 dark:text-blue-600'
                : 'ml-2 flex-shrink-0 text-yellow-600 dark:text-yellow-600'
              }
            >
              <div className="flex flex-row gap-x-2">
                <IconSparkles
                  className={
                    stepHealSuccess ? 'text-blue-600 dark:text-blue-600' : 'text-yellow-600 dark:text-yellow-600'
                  }
                  size={14}
                />
              </div>
            </Badge>
          </Tooltip>
        )}
      </div>

      <ActionIcon
        size="xs"
        variant="transparent"
        color="gray"
        className="ml-2 flex-shrink-0"
      >
        <IconChevronDown
          className={clsx('transition-transform', {
            'rotate-180': expanded,
          })}
          size={14}
        />
      </ActionIcon>
    </div>
  );
}
